import { COLLAPSE_DURATION, Card, Collapse, Icon, IconButton, Price, SectionHeader, SelectableCard, Slider, Switch, Text } from "@/components/ui"
import { Minus, Plus, StarIcon } from "@/icons"
import { getStoredCompany } from "@/lib/company-storage"
import { formatCouponAvailabilityHint } from "@/lib/coupon-availability"
import { formatBRL } from "@/lib/format-brl"
import { formatPoints } from "@/lib/format-points"
import { redemptionAllowedAt } from "@/lib/loyalty-benefits"
import { savePendingCouponCodes } from "@/lib/pending-coupon"
import { useAtom } from "jotai"
import { useDiscountPlan } from "../hooks/useDiscountPlan"
import { benefitClaimInstants } from "../lib/checkout-pricing"
import { availableLoyaltyPoints, checkoutAtom, clampLoyaltyPoints } from "../state"

function PlainLine({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="flex items-start gap-3">
      <Icon as={StarIcon} size="xl" tone="secondary" className="mt-0.5" />
      <div className="min-w-0">
        <Text size="sm" weight="semibold" tone="default">
          {title}
        </Text>
        {detail ? (
          <Text size="sm" tone="muted">
            {detail}
          </Text>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Loyalty points as money on the extras step: hidden without a balance; one clear line
 * with a switch to use points instead when a coupon is on the order (points as a
 * discount do not add up with a coupon; benefits and rewards do); otherwise the
 * toggle and the amount slider. Nothing truncated.
 */
export function LoyaltyPointsSection() {
  const [state, setState] = useAtom(checkoutAtom)
  const { pricing } = useDiscountPlan()
  const program = state.loyalty.program
  const pointsBalance = availableLoyaltyPoints(state)
  const couponApplied = state.couponCodes.length > 0
  const claimAt = benefitClaimInstants(state)
  const pointsAvailability = state.benefits.active?.pointsProgram?.availability
  const pointsWindowOpen = claimAt.every((instant) => redemptionAllowedAt(pointsAvailability, instant))
  const maxRedeemable = pricing.maxRedeemablePoints
  const redeemable = pricing.canRedeemPoints && pointsWindowOpen
  const pointsToUse = pricing.pointsUsed
  const loyaltyDiscount = pointsToUse * program.pointsValueInCurrency
  const pointsStep = Math.max(1, Math.round((maxRedeemable - program.minimumPointsToRedeem) / 10))
  const balance = formatPoints(pointsBalance, "pontos")

  if (!program.active || pointsBalance <= 0) return null

  function choosePoints() {
    setState((prev) => ({ ...prev, couponCodes: [], loyalty: { ...prev.loyalty, use: true, pointsToUse: maxRedeemable } }))
    const merchantId = getStoredCompany()?.id
    if (merchantId) savePendingCouponCodes([], merchantId)
  }

  function toggleLoyalty() {
    setState((prev) => ({
      ...prev,
      loyalty: { ...prev.loyalty, use: !prev.loyalty.use, pointsToUse: prev.loyalty.use ? prev.loyalty.pointsToUse : maxRedeemable },
    }))
  }

  function changePointsToUse(next: number) {
    setState((prev) => ({ ...prev, loyalty: { ...prev.loyalty, pointsToUse: clampLoyaltyPoints(prev, pricing.payableItems, next) } }))
  }

  function unavailableReason(): string {
    if (!pointsWindowOpen) {
      const hint = formatCouponAvailabilityHint(pointsAvailability ?? undefined)
      return hint ? `Resgate de pontos ${hint.charAt(0).toLowerCase()}${hint.slice(1)}` : "Resgate de pontos indisponível agora"
    }
    if (pointsBalance < program.minimumPointsToRedeem) return `Mínimo de ${formatPoints(program.minimumPointsToRedeem, "pontos")} para resgatar`
    return `Pedido mínimo de ${formatBRL(program.minimumPointsToRedeem * program.pointsValueInCurrency)} para usar pontos`
  }

  return (
    <section aria-label="Pontos de fidelidade">
      <SectionHeader title="Pontos" />
      {couponApplied && !state.loyalty.use ? (
        <div className="flex flex-col gap-3">
          <PlainLine
            title="Pontos como desconto não somam com cupom."
            detail={`Você tem ${balance}. Prêmios e benefícios da fidelidade continuam valendo com o cupom.`}
          />
          {redeemable ? (
            <Switch checked={false} onCheckedChange={(next) => (next ? choosePoints() : undefined)} label="Usar pontos no lugar do cupom" tone="secondary" />
          ) : null}
        </div>
      ) : !redeemable ? (
        <PlainLine title={unavailableReason()} detail={`Você tem ${balance}.`} />
      ) : (
        <>
          <SelectableCard
            selected={state.loyalty.use}
            indicator="radio"
            tone="secondary"
            onSelect={toggleLoyalty}
            icon={<Icon as={StarIcon} size="2xl" tone="secondary" />}
            title={state.loyalty.use ? `Usando ${formatPoints(pointsToUse, "pontos")}` : "Usar pontos de fidelidade"}
            subtitle={state.loyalty.use ? `Você economiza ${formatBRL(loyaltyDiscount)}` : `Você tem ${balance}`}
          />
          <Collapse open={state.loyalty.use} enterDelay={COLLAPSE_DURATION}>
            <Card padding="lg" className="mt-4">
              <div className="mb-3 flex items-center justify-between">
                <Text as="label" htmlFor="loyalty-points-range" weight="semibold" tone="default">
                  Quantos pontos usar?
                </Text>
                <Price value={loyaltyDiscount} sign="minus" tone="secondary" />
              </div>
              <div className="flex items-center gap-3">
                <IconButton
                  icon={Minus}
                  label="Usar menos pontos"
                  variant="outline"
                  tone="primary"
                  onClick={() => changePointsToUse(pointsToUse - pointsStep)}
                  disabled={pointsToUse <= program.minimumPointsToRedeem}
                />
                <Slider
                  id="loyalty-points-range"
                  min={program.minimumPointsToRedeem}
                  max={maxRedeemable}
                  step={1}
                  value={pointsToUse}
                  onValueChange={changePointsToUse}
                  className="flex-1"
                />
                <IconButton
                  icon={Plus}
                  label="Usar mais pontos"
                  variant="outline"
                  tone="primary"
                  onClick={() => changePointsToUse(pointsToUse + pointsStep)}
                  disabled={pointsToUse >= maxRedeemable}
                />
              </div>
              <Text as="div" size="sm" tone="muted" className="mt-2 flex flex-wrap items-center justify-between gap-x-3">
                <span>
                  {formatPoints(pointsToUse)} de {balance}
                </span>
                <span>máx. {formatPoints(maxRedeemable)} neste pedido</span>
              </Text>
            </Card>
          </Collapse>
        </>
      )}
    </section>
  )
}
